import * as React from 'react'

/**
 * Track whether a one-line element is actually clipped by its container,
 * recomputed when `dep` (the content) changes and on resize.
 *
 * Powers "reveal the full text" affordances on truncating elements — a hover
 * tooltip or a click popover — and, because the clipped state is known up front,
 * lets a non-interactive element become focusable only when there's something to
 * reveal (rather than adding a dead tab stop to every short label). Attach the
 * returned `ref` to the `truncate` element.
 *
 * @param dep - The content; the measurement recomputes when it changes.
 * @returns `[ref, truncated]` — the ref to attach and whether it's clipped.
 */
export function useTruncation<T extends HTMLElement>(dep: unknown) {
  const ref = React.useRef<T>(null)
  const [truncated, setTruncated] = React.useState(false)
  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => setTruncated(el.scrollWidth > el.clientWidth)
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [dep])
  return [ref, truncated] as const
}
